import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  productsApi,
  publishApi,
  schedulesApi,
  categoriesApi,
  type Product,
  type ProductUpdate,
  type Schedule,
  type ConsistItem,
  type ColorOption,
  ApiError,
} from '../lib/api';
import { useBasePath, withBase } from '../lib/basePath';

// Modal-flavored product editor. Shares the same backend (PUT /api/products/:id)
// and image-upload pipeline as the dedicated /products/:id page; rendering is
// just compacter (two-column layout, no breadcrumbs, no debug panels).

type Props = {
  id: number;
  onClose: () => void;
  onChanged?: (product: Product) => void;
};

const THEMATIC_OPTIONS = [
  { id: 53,  label: 'Последний звонок' },
  { id: 102, label: 'Сезонные цветы' },
  { id: 39,  label: 'Пиономания' },
  { id: 137, label: 'Корпоративные подарки' },
];

export default function ProductDetailModal({ id, onClose, onChanged }: Props) {
  const base = useBasePath();

  const [product, setProduct] = useState<Product | null>(null);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [colorOptions, setColorOptions] = useState<ColorOption[]>([]);

  // Editable fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('0');
  const [scheduleId, setScheduleId] = useState<number | null>(null);
  const [isActive, setIsActive] = useState(true);
  const [minOrderCount, setMinOrderCount] = useState('1');
  const [stockQty, setStockQty] = useState('');
  const [bunchSize, setBunchSize] = useState('');
  const [height, setHeight] = useState('');
  const [width, setWidth] = useState('');
  const [manufacturerName, setManufacturerName] = useState('');
  const [manufacturerCountryId, setManufacturerCountryId] = useState('1');
  const [vatId, setVatId] = useState('');
  const [colorId, setColorId] = useState('');
  const [discountPercent, setDiscountPercent] = useState('');
  const [thematic, setThematic] = useState<Set<number>>(new Set());
  const [consist, setConsist] = useState<ConsistItem[]>([]);
  const [images, setImages] = useState<string[]>([]);

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [publishing, setPublishing] = useState<'dry' | 'real' | null>(null);
  const [publishMsg, setPublishMsg] = useState<string | null>(null);

  const fileRef = useRef<HTMLInputElement>(null);
  const [imgBusy, setImgBusy] = useState(false);

  // ESC closes the modal.
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  // Lock body scroll while open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Initial load.
  useEffect(() => {
    productsApi.get(id)
      .then(({ product: p }) => {
        setProduct(p);
        setName(p.name);
        setDescription(p.description ?? '');
        setPrice(String(p.price_rub));
        setScheduleId(p.schedule_group_id);
        setIsActive(p.is_active);
        setMinOrderCount(p.min_order_count?.toString() ?? '1');
        setStockQty(p.stock_qty?.toString() ?? '');
        setBunchSize(p.bunch_size?.toString() ?? '');
        setHeight(String(p.properties.height ?? ''));
        setWidth(String(p.properties.width ?? ''));
        setManufacturerName(String(p.properties.manufacturerName ?? ''));
        setManufacturerCountryId(p.manufacturer_country_id?.toString() ?? '1');
        setVatId(p.vat_id?.toString() ?? '');
        setColorId(p.color_id?.toString() ?? '');
        setDiscountPercent(p.discount_percent?.toString() ?? '');
        setThematic(new Set(p.thematic_selections ?? []));
        setConsist(p.consist);
        setImages(p.images);
        schedulesApi.list(p.seller_id).then((r) => setSchedules(r.schedules)).catch(() => { /* noop */ });
        // Lazy-fetch the color palette once per modal open. Cached server-side
        // for 24h so the cost is one Flowwow round-trip per day at most.
        categoriesApi.colors().then((r) => setColorOptions(r.colors)).catch(() => { /* keep input usable even on failure */ });
      })
      .catch((e) => setErr(String(e?.message ?? e)));
  }, [id]);

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!product) return;
    setBusy(true);
    setErr(null);
    try {
      const props: Record<string, string> = { ...(product.properties as Record<string, string>) };
      if (height) props.height = height; else delete props.height;
      if (width)  props.width  = width;  else delete props.width;
      if (manufacturerName) props.manufacturerName = manufacturerName; else delete props.manufacturerName;

      const patch: ProductUpdate = {
        name: name.trim(),
        description: description.trim() || null,
        price_rub: Math.round(Number(price)) || 0, // server rejects non-integer (zod .int())
        schedule_group_id: scheduleId,
        is_active: isActive,
        min_order_count:         minOrderCount ? Number(minOrderCount) : null,
        stock_qty:               stockQty ? Number(stockQty) : null,
        bunch_size:              bunchSize ? Number(bunchSize) : null,
        manufacturer_country_id: manufacturerCountryId ? Number(manufacturerCountryId) : null,
        vat_id:                  vatId ? Number(vatId) : null,
        color_id:                colorId ? Number(colorId) : null,
        discount_percent:        discountPercent ? Number(discountPercent) : null,
        thematic_selections:     [...thematic].sort((a, b) => a - b),
        properties: props,
        consist,
      };
      const r = await productsApi.update(id, patch);
      onChanged?.(r.product);
      // Close on success so the seller goes back to the list. The list
      // already reflects the change via onChanged.
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) setErr('Артикул уже занят у этого продавца');
      else setErr(String((e as Error)?.message ?? e));
    } finally {
      setBusy(false);
    }
  }

  async function uploadImages(files: FileList | null) {
    if (!files || !files.length) return;
    setImgBusy(true);
    setErr(null);
    try {
      console.log('[modal upload] files:', Array.from(files).map((f) => `${f.name} ${(f.size / 1024 / 1024).toFixed(1)}MB ${f.type}`));
      const r = await productsApi.uploadImages(id, Array.from(files));
      console.log('[modal upload] response', r);
      setImages(r.images);
      if (product) {
        const next = { ...product, images: r.images };
        setProduct(next);
        onChanged?.(next);
      }
      if (r.errors && r.errors.length) {
        setErr(`Часть фото не загрузилась: ${r.errors.map((e: { filename?: string; error?: string }) => `${e.filename}: ${e.error}`).join('; ')}`);
      } else if (r.added.length === 0) {
        setErr('Ни одно фото не загрузилось. Возможно файл повреждён.');
      }
    } catch (e) {
      console.error('[modal upload] failed', e);
      const ae = e as { status?: number; message?: string };
      setErr(`Загрузка не удалась: ${ae.message ?? e}${ae.status ? ` (HTTP ${ae.status})` : ''}`);
    } finally {
      setImgBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }
  async function deleteImage(idx: number) {
    const r = await productsApi.deleteImage(id, idx);
    setImages(r.images);
    if (product) {
      const next = { ...product, images: r.images };
      setProduct(next);
      onChanged?.(next);
    }
  }
  async function moveImage(idx: number, dir: -1 | 1) {
    const t = idx + dir;
    if (t < 0 || t >= images.length) return;
    const order = images.map((_, i) => i);
    [order[idx], order[t]] = [order[t], order[idx]];
    const r = await productsApi.reorderImages(id, order);
    setImages(r.images);
  }

  // Drag-and-drop reorder. Mobile keeps the ←/→ arrows as a fallback because
  // HTML5 DnD is desktop-only; touchstart-based reorder would be a separate
  // implementation. dragIndex = source, dragOverIndex = where the indicator
  // currently sits so we can highlight the drop target.
  const [dragIndex, setDragIndex]         = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  async function onDropPhoto(targetIdx: number) {
    const from = dragIndex;
    setDragIndex(null);
    setDragOverIndex(null);
    if (from == null || from === targetIdx) return;
    const order = images.map((_, i) => i);
    const [moved] = order.splice(from, 1);
    order.splice(targetIdx, 0, moved);
    try {
      const r = await productsApi.reorderImages(id, order);
      setImages(r.images);
      if (product) {
        const next = { ...product, images: r.images };
        setProduct(next);
        onChanged?.(next);
      }
    } catch (e) {
      alert(`Не удалось переставить: ${String((e as Error)?.message ?? e)}`);
    }
  }

  async function onPublish(mode: 'dry' | 'real') {
    setPublishing(mode);
    setPublishMsg(null);
    try {
      const r = await publishApi.one(id, mode === 'dry');
      if (mode === 'dry') {
        const pt = 'productionTimeHours' in r ? r.productionTimeHours : null;
        setPublishMsg(`Превью OK. productionTime = ${pt ?? '—'} ч.`);
      } else {
        setPublishMsg('Отправлено на Flowwow ✓');
      }
      const fresh = await productsApi.get(id);
      setProduct(fresh.product);
      onChanged?.(fresh.product);
    } catch (e) {
      if (e instanceof ApiError) {
        const body = (e.body ?? {}) as Record<string, unknown>;
        const fw = body.flowwow_body as { message?: string; fields?: Array<{ path: string; message: string }> } | undefined;
        const f = fw?.fields?.map((x) => `${x.path}: ${x.message}`).join('; ');
        setPublishMsg(`Ошибка ${e.status}: ${fw?.message ?? e.message}${f ? ` (${f})` : ''}`);
      } else setPublishMsg(String((e as Error)?.message ?? e));
    } finally {
      setPublishing(null);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>{product?.name ?? `Товар #${id}`}</h3>
          <div className="row">
            <Link to={withBase(base, `/products/${id}`)} className="btn" onClick={onClose}>Открыть на странице</Link>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Закрыть">✕</button>
          </div>
        </div>

        <form className="modal-body form" onSubmit={save}>
          {!product && !err && <p className="muted">Загрузка…</p>}

          {err && <div className="error">{err}</div>}

          {product && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 22 }}>
                {/* Left: основные поля */}
                <div className="form">
                  <label>
                    <span>Название *</span>
                    <input value={name} onChange={(e) => setName(e.target.value)} required />
                  </label>

                  <label>
                    <span>Описание</span>
                    <textarea rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
                  </label>

                  <div className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
                    <label style={{ flex: 1 }}>
                      <span>Цена, ₽ *</span>
                      <input type="number" min={0} value={price} onChange={(e) => setPrice(e.target.value)} required />
                    </label>
                    <label style={{ flex: 1 }}>
                      <span>Скидка, %</span>
                      <input type="number" min={0} max={99} value={discountPercent} onChange={(e) => setDiscountPercent(e.target.value)} />
                    </label>
                  </div>

                  <div className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
                    <label style={{ flex: 1 }}>
                      <span>Наличие, шт *</span>
                      <input type="number" min={0} value={stockQty} onChange={(e) => setStockQty(e.target.value)} />
                    </label>
                    <label style={{ flex: 1 }}>
                      <span>В упаковке (цветов) *</span>
                      <input type="number" min={1} value={bunchSize} onChange={(e) => setBunchSize(e.target.value)} />
                    </label>
                    <label style={{ flex: 1 }}>
                      <span>Мин. заказ *</span>
                      <input type="number" min={1} value={minOrderCount} onChange={(e) => setMinOrderCount(e.target.value)} />
                    </label>
                  </div>

                  <div className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
                    <label style={{ flex: 1 }}>
                      <span>Высота, см *</span>
                      <input type="number" min={1} value={height} onChange={(e) => setHeight(e.target.value)} />
                    </label>
                    <label style={{ flex: 1 }}>
                      <span>Ширина, см</span>
                      <input type="number" min={1} value={width} onChange={(e) => setWidth(e.target.value)} />
                    </label>
                  </div>

                  <label>
                    <span>Расписание отгрузок</span>
                    <select value={scheduleId ?? ''} onChange={(e) => setScheduleId(e.target.value ? Number(e.target.value) : null)}>
                      <option value="">— без расписания —</option>
                      {schedules.map((s) => (
                        <option key={s.id} value={s.id}>{s.name}{s.is_default ? ' (по умолч.)' : ''}</option>
                      ))}
                    </select>
                  </label>

                  <label className="row">
                    <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                    <span>Активен (публикуется и обновляется воркером)</span>
                  </label>
                </div>

                {/* Right: фото + дополнительные поля */}
                <div className="form">
                  <fieldset className="fieldset">
                    <legend>Фото</legend>
                    <div className="row">
                      <input
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => uploadImages(e.target.files)}
                        disabled={imgBusy}
                      />
                      <span className="muted" style={{ fontSize: 12 }}>JPG/PNG → WebP, до 12 за раз</span>
                    </div>
                    {images.length === 0 ? (
                      <p className="muted">Картинок пока нет.</p>
                    ) : (
                      <div className="image-grid">
                        {images.map((url, idx) => {
                          const isDragging = dragIndex === idx;
                          const isDropTarget = dragOverIndex === idx && dragIndex != null && dragIndex !== idx;
                          return (
                            <div
                              key={url}
                              className={`image-tile${isDragging ? ' dragging' : ''}${isDropTarget ? ' drop-target' : ''}`}
                              draggable
                              onDragStart={(e) => {
                                setDragIndex(idx);
                                e.dataTransfer.effectAllowed = 'move';
                                // Some browsers ignore drag without data — set a placeholder.
                                e.dataTransfer.setData('text/plain', String(idx));
                              }}
                              onDragOver={(e) => {
                                e.preventDefault();
                                e.dataTransfer.dropEffect = 'move';
                                if (dragOverIndex !== idx) setDragOverIndex(idx);
                              }}
                              onDragLeave={() => {
                                if (dragOverIndex === idx) setDragOverIndex(null);
                              }}
                              onDrop={(e) => {
                                e.preventDefault();
                                onDropPhoto(idx);
                              }}
                              onDragEnd={() => {
                                setDragIndex(null);
                                setDragOverIndex(null);
                              }}
                              title="Перетащи, чтобы переставить"
                            >
                              <img src={url} alt="" />
                              <div className="image-tile-tools">
                                <button type="button" className="btn" onClick={() => moveImage(idx, -1)} disabled={idx === 0}>←</button>
                                <span className="muted">{idx + 1}</span>
                                <button type="button" className="btn" onClick={() => moveImage(idx, 1)} disabled={idx === images.length - 1}>→</button>
                                <button type="button" className="btn" onClick={() => deleteImage(idx)}>✕</button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </fieldset>

                  <fieldset className="fieldset">
                    <legend>Производитель</legend>
                    <label>
                      <span>Имя</span>
                      <input value={manufacturerName} onChange={(e) => setManufacturerName(e.target.value)} placeholder="Florentika" />
                    </label>
                    <div className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
                      <label style={{ flex: 1 }}>
                        <span>Страна</span>
                        <select value={manufacturerCountryId} onChange={(e) => setManufacturerCountryId(e.target.value)}>
                          <option value="">— не указано —</option>
                          <option value="1">Россия</option>
                          <option value="3">Беларусь</option>
                          <option value="4">Казахстан</option>
                          <option value="11">Кыргызстан</option>
                          <option value="18">Узбекистан</option>
                        </select>
                      </label>
                      <label style={{ flex: 1 }}>
                        <span>НДС</span>
                        <select value={vatId} onChange={(e) => setVatId(e.target.value)}>
                          <option value="">— не указано —</option>
                          <option value="10">10%</option>
                          <option value="22">22%</option>
                        </select>
                      </label>
                      <label style={{ flex: 1 }}>
                        <span>
                          Цвет
                          {colorId && (
                            <button
                              type="button"
                              className="link-btn"
                              style={{ marginLeft: 8, fontSize: 11 }}
                              onClick={() => setColorId('')}
                            >× сброс</button>
                          )}
                        </span>
                        {colorOptions.length === 0 ? (
                          <input
                            type="number"
                            min={1}
                            value={colorId}
                            onChange={(e) => setColorId(e.target.value)}
                            placeholder="ID цвета (Flowwow enum пока не загружен)"
                          />
                        ) : (
                          <div className="color-grid">
                            {colorOptions.map((c) => {
                              const active = String(c.id) === colorId;
                              const bg = c.hex === 'gradient' || !c.hex
                                ? 'linear-gradient(135deg,#f1c40f,#e67e22,#e74c3c,#9b59b6,#3498db,#27ae60)'
                                : c.hex;
                              return (
                                <button
                                  key={c.id}
                                  type="button"
                                  className={`color-swatch${active ? ' active' : ''}`}
                                  onClick={() => setColorId(active ? '' : String(c.id))}
                                  title={c.name}
                                >
                                  <span className="color-chip" style={{ background: bg }} />
                                  <span className="color-name">{c.name}</span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </label>
                    </div>
                  </fieldset>

                  <fieldset className="fieldset">
                    <legend>Тематические подборки</legend>
                    <div className="weekday-row">
                      {THEMATIC_OPTIONS.map((t) => (
                        <label key={t.id} className={`chip ${thematic.has(t.id) ? 'on' : ''}`}>
                          <input
                            type="checkbox"
                            checked={thematic.has(t.id)}
                            onChange={() => {
                              const next = new Set(thematic);
                              if (next.has(t.id)) next.delete(t.id); else next.add(t.id);
                              setThematic(next);
                            }}
                          />
                          <span>{t.label}</span>
                        </label>
                      ))}
                    </div>
                  </fieldset>
                </div>
              </div>

              {/* Publish row */}
              {product && (
                <div className="card sub" style={{ marginTop: 4 }}>
                  <div className="row" style={{ justifyContent: 'space-between' }}>
                    <div>
                      <strong>Публикация на Flowwow:</strong>{' '}
                      {product.flowwow_product_id
                        ? <code>productId {product.flowwow_product_id}</code>
                        : <span className="muted">ещё не публиковали</span>}
                      {product.last_pushed_at && (
                        <span className="muted" style={{ marginLeft: 10 }}>
                          посл.: {product.last_pushed_at.slice(0, 16)} {product.last_push_status === 'ok' ? '✓' : `(${product.last_push_status})`}
                        </span>
                      )}
                    </div>
                    <div className="row">
                      <button type="button" className="btn" disabled={publishing !== null} onClick={() => onPublish('dry')}>
                        {publishing === 'dry' ? '…' : 'Превью payload'}
                      </button>
                      <button type="button" className="btn primary" disabled={publishing !== null} onClick={() => onPublish('real')}>
                        {publishing === 'real' ? '…' : 'Опубликовать'}
                      </button>
                    </div>
                  </div>
                  {publishMsg && <p className="muted" style={{ marginTop: 8 }}>{publishMsg}</p>}
                </div>
              )}

              {/* Bottom action bar */}
              <div className="row" style={{ justifyContent: 'flex-end', borderTop: '1px solid var(--brand-line)', paddingTop: 14 }}>
                <button type="button" className="btn" onClick={onClose}>Отмена</button>
                <button type="submit" className="btn primary" disabled={busy}>
                  {busy ? 'Сохраняем…' : 'Сохранить'}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
